import { useMemo, useState } from 'react';
import { api } from '@/core/api/queries';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RejectionDialog } from '@/shared/components/dialogs/RejectionDialog';
import { ReportPageLayout } from '@/shared/components/layouts';
import { EditionSelector } from '@/shared/components/tables';
import { useReportPage } from '@/shared/hooks';
import { columnBuilder } from '@/shared/utils';
import { formatUnixTimestamp } from '@/shared/utils/formatting';
import { RedemptionStatusBadge } from '../components/RedemptionStatusBadge';
import { useTicketMutations } from '../hooks/use-ticket-mutations';
import type { TicketRedemptionDto, TicketRedemptionStatus } from '../api/tickets.api';

/** Backendul are ORDER BY fix si doar filtrele de editie + stare: nicio coloana nu sorteaza/filtreaza. */
const FIXA = { sortable: false, filterable: false } as const;

/**
 * Raportul de revendicari (spec §8) - DBEST/ADMIN.
 *
 * Pe tiparul comun (`useReportPage` + `ReportPageLayout`), cu export Excel. Pe mobil ramane
 * rezumatul standard (decizia owner-ului): actiunile se fac de pe desktop.
 *
 * Zero `window.prompt` pentru motivul anularii: blocheaza automatizarea de browser si e
 * inutilizabil pe mobil. Motivul e OBLIGATORIU - backendul refuza altfel cu 400.
 */
export default function TicketRedemptionsReportPage() {
    const { report, adapters, handleExport, editions, currentEdition, changeEdition } = useReportPage<TicketRedemptionDto>({
        queryHook: api.tickets.redemptions,
        exportEndpoint: '/api/ticket-redemptions',
        exportFilename: 'raport_revendicari_premii',
        withEditions: true,
        initialPageSize: 50,
    });
    const [deAnulat, setDeAnulat] = useState<TicketRedemptionDto | null>(null);
    const { updateStatus } = useTicketMutations();

    const status = (report.filter.status as TicketRedemptionStatus | undefined) ?? 'ALL';

    const columns = useMemo(() => {
        // SE-18: saltul direct REQUESTED -> FULFILLED a iesit din UI. Doi pasi, fiecare cu
        // propriul buton: Confirmă (-> CONFIRMED), apoi Marchează trimis (-> FULFILLED).
        const confirma = (id: string) => updateStatus.mutate({ id, body: { status: 'CONFIRMED' } });
        const marcheazaTrimis = (id: string) => updateStatus.mutate({ id, body: { status: 'FULFILLED' } });

        return columnBuilder<TicketRedemptionDto>()
            .text('schoolName', 'Școala', undefined, { ...FIXA, cardRole: 'title' })
            .custom({
                key: 'tierName', label: 'Nivel', ...FIXA,
                render: (_, r) => `${r.tierLevel ?? ''} · ${r.tierName ?? ''}`,
            })
            .number('pointsSpent', 'Puncte', undefined, { ...FIXA, cardRole: 'value' })
            .text('redeemedByName', 'Solicitat de', undefined, FIXA)
            // `alegereProfesor` (ce a ales profesorul) e DISTINCT de `note` (motivul adminului la anulare).
            .text('alegereProfesor', 'Alegere', undefined, {
                ...FIXA,
                // Trunchiat, cu textul intreg in `title`: un raspuns lung impingea «Acțiuni» afara din ecran.
                render: (v) => (v ? <span className="block max-w-48 truncate" title={v}>{v}</span> : ''),
            })
            .custom({
                key: 'redeemedDt', label: 'Data', ...FIXA,
                render: (v) => (v ? formatUnixTimestamp(v) : ''),
            })
            .custom({
                key: 'status', label: 'Stare', ...FIXA,
                render: (_, r) => (
                    <>
                        <RedemptionStatusBadge status={r.status ?? 'REQUESTED'} />
                        {r.status === 'CANCELLED' && r.note && (
                            <span className="block text-xs text-muted-foreground">{r.note}</span>
                        )}
                    </>
                ),
            })
            .action('actiuni', 'Acțiuni', (_, r) => (
                <div className="flex gap-2">
                    {r.status === 'REQUESTED' && (
                        <Button size="sm" onClick={() => confirma(r.id!)}>Confirmă</Button>
                    )}
                    {r.status === 'CONFIRMED' && (
                        <Button size="sm" onClick={() => marcheazaTrimis(r.id!)}>Marchează trimis</Button>
                    )}
                    {r.status !== 'CANCELLED' && (
                        <Button size="sm" variant="outline" onClick={() => setDeAnulat(r)}>Anulează</Button>
                    )}
                </div>
            ))
            .build();
    }, [updateStatus.mutate]);

    return (
        /* Celule compacte si text care se rupe (ca la `RecipienteAdminPage`): cu padding-ul
           implicit de 24 px si `nowrap`, «Acțiuni» iesea din ecran la 1280. `contents` nu
           adauga cutie in lantul de inaltime al layoutului. */
        <div className="contents [&_td]:whitespace-normal [&_td]:px-3 [&_th]:px-3">
            <ReportPageLayout
                title="Revendicări de premii"
                report={report}
                adapters={adapters}
                handleExport={handleExport}
                columns={columns}
                entityLabel="revendicări"
                unitLabel="puncte"
                emptyMessage="Nicio revendicare pentru filtrele alese."
                extraControls={
                    <div className="flex flex-col gap-2 sm:flex-row">
                        <EditionSelector
                            editions={editions ?? []}
                            currentEditionId={currentEdition?.id}
                            onChange={(id) => changeEdition?.(id)}
                        />
                        <Select
                            value={status}
                            onValueChange={(v) =>
                                report.updateFilter({ status: v === 'ALL' ? undefined : (v as TicketRedemptionStatus) })
                            }
                        >
                            <SelectTrigger className="min-h-12 w-full sm:w-48" aria-label="Stare">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="ALL">Toate stările</SelectItem>
                                <SelectItem value="REQUESTED">Solicitate</SelectItem>
                                <SelectItem value="CONFIRMED">Confirmate</SelectItem>
                                <SelectItem value="FULFILLED">Livrate</SelectItem>
                                <SelectItem value="CANCELLED">Anulate</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                }
            />

            <RejectionDialog
                isOpen={deAnulat !== null}
                onClose={() => setDeAnulat(null)}
                loading={updateStatus.isPending}
                title="Anulezi revendicarea?"
                description={
                    deAnulat
                        ? `„${deAnulat.tierName}" pentru ${deAnulat.schoolName}. Cele ${deAnulat.pointsSpent} puncte revin imediat în soldul școlii.`
                        : ''
                }
                warningMessage="Motivul e obligatoriu și ajunge la coordonator — pentru el, soldul crește fără altă explicație."
                rejectButtonText="Anulează revendicarea"
                // 🔴 Implicit, butonul de renuntare al RejectionDialog scrie „Anulare" - langa
                // „Anuleaza revendicarea" devin doua butoane care incep la fel si fac opusul.
                // Gasit la verificarea pe DEV, 2026-09-01.
                cancelButtonText="Renunț"
                onReject={async (reason) => {
                    if (!deAnulat?.id) return;
                    await updateStatus.mutateAsync({ id: deAnulat.id, body: { status: 'CANCELLED', note: reason } });
                    setDeAnulat(null);
                }}
            />
        </div>
    );
}
